import SystemConfig from "../system/SystemConfig";
import SERVICE_IDENTIFIER from "../constants/identifiers";
import { Container } from "inversify";
import Settings from "./Settings";
import * as Sentry from "@sentry/browser";
import { BrowserTracing } from "@sentry/tracing";
import version from "../../version";
import { defineCustomSystemParts } from "../system/defineSystem";
import type Wallet from "../wallet/Wallet";
import type GameSlots from "../game/slot/GameSlots";
import { CONFIG_PARAMS } from "../system/SystemConfig";

export default async function initSettings(container: Container) {
  initSentry(container);
  version();

  container
    .bind<Settings>(SERVICE_IDENTIFIER.Settings)
    .to(Settings)
    .inSingletonScope();

  let config = container.get<SystemConfig>(SERVICE_IDENTIFIER.SystemConfig);
  let settings = container.get<Settings>(SERVICE_IDENTIFIER.Settings);
  await settings.init(container);

  config.initSettings(settings);
  defineCustomSystemParts(container, settings);
}

function initSentry(container: Container): void {
  try {
    const dsn = process.env.SENTRY_DSN;
    const environment = process.env.ENVIRONMENT;
    const release = process.env.RELEASE;
    if (dsn) {
      Sentry.init({
        dsn: dsn,
        integrations: [
          Sentry.thirdPartyErrorFilterIntegration({
            filterKeys: [process.env.SENTRY_APP_KEY ?? ""],
            behaviour: "apply-tag-if-contains-third-party-frames",
          }),
        ],
				ignoreErrors:["Game session already closed, please start the game from casino lobby again.", "Problems with wallet server communication"],
        release,
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
        environment: environment,
        beforeSend(event, hint) {
          const config = container.get<SystemConfig>(
            SERVICE_IDENTIFIER.SystemConfig,
          );
          const wallet = container.get<Wallet>(SERVICE_IDENTIFIER.Wallet);
          const game = container.get<GameSlots>(SERVICE_IDENTIFIER.Game);
          const searchableContext = {
            playerId: config.get(CONFIG_PARAMS.PLAYER_ID),
            casinoId: config.get(CONFIG_PARAMS.CASINO_ID),
            sessionId: config.get(CONFIG_PARAMS.SESSION_ID),
            gameId: config.get(CONFIG_PARAMS.GAME_ID),
            currency: config.get(CONFIG_PARAMS.CURRENCY),
            isBonus: wallet.isBonus(),
            bet: wallet.getBet(),
            roundId: game.round.getId(),
            mode: config.get(CONFIG_PARAMS.MODE),
          };

          const gameTags: Record<string, string> = {};
          Object.keys(searchableContext).forEach((key) => {
            const value =
              searchableContext[key as keyof typeof searchableContext];
            if (value !== null && value !== undefined) {
              gameTags[key] = String(value);
            }
          });
          event.tags = {
            ...event.tags,
            ...gameTags,
          };

          const diagnosticContext: Record<string, unknown> = {
            wallet: {
              totalBet: wallet.getTotalBet(),
              win: wallet.getWin(),
              linesCount: wallet.getLinesCount(),
              freeSpins: wallet.getFreeSpins(),
            },
            gameplay: {
              resultIndex: game.round.getResultIndex(),
              roundRunning: game.round.isRunning(),
              unfinishedRound: game.round.isUnfinished(),
              autoRun: game.isAutoRun(),
              inFreeSpins: game.isInFreeSpins(),
            },
          };

          const rejectionEvent = getRejectedEventContext(
            hint?.originalException,
          );
          if (rejectionEvent) {
            diagnosticContext.rejectionEvent = rejectionEvent;
          }

          event.extra = {
            ...event.extra,
            ...diagnosticContext,
          };
          return event;
        },
      });
    }
  } catch (e) {
    console.log(e);
  }
}

function getRejectedEventContext(
  originalException: unknown,
): Record<string, unknown> | undefined {
  if (!(originalException instanceof Event)) {
    return undefined;
  }

  const target = originalException.target;
  const element = target instanceof Element ? target : undefined;
  const assetUrl = getEventTargetUrl(target);

  return {
    type: originalException.type,
    targetType: target?.constructor?.name,
    tagName: element?.tagName,
    id: element?.id || undefined,
    assetUrl,
    isTrusted: originalException.isTrusted,
  };
}

function getEventTargetUrl(target: EventTarget | null): string | undefined {
  if (!target) {
    return undefined;
  }

  let value: string | undefined;
  if (target instanceof HTMLMediaElement || target instanceof HTMLImageElement) {
    value = target.currentSrc || target.src;
  } else if (target instanceof HTMLScriptElement) {
    value = target.src;
  } else if (target instanceof HTMLLinkElement) {
    value = target.href;
  }

  if (!value) {
    return undefined;
  }

  try {
    const url = new URL(value, window.location.href);
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return undefined;
  }
}
